/**
 * Country reference utilities — DB-backed.
 *
 * The database (`transaction_countries`) is the single source of truth for the
 * country list. This module keeps an in-memory registry that is:
 *   1. hydrated synchronously from localStorage at import time (instant on repeat
 *      visits), and
 *   2. refreshed from GET /api/reference/countries by <CountriesProvider>.
 *
 * There is intentionally NO hardcoded country table here anymore.
 */

import 'flag-icons/css/flag-icons.min.css';

const CACHE_KEY = 'pg_countries_ref_v1';

let _nameByCode = {};      // alpha-2 -> name
let _nameByAlpha3 = {};     // alpha-3 -> name
let _alpha2ByAlpha3 = {};   // alpha-3 -> alpha-2
let _list = [];             // [{ code, alpha3, name }]

const _rebuild = (list) => {
  _list = Array.isArray(list) ? list : [];
  _nameByCode = {};
  _nameByAlpha3 = {};
  _alpha2ByAlpha3 = {};
  for (const c of _list) {
    const code = (c.code || '').toUpperCase();
    const a3 = (c.alpha3 || '').toUpperCase();
    if (code && c.name) _nameByCode[code] = c.name;
    if (a3 && c.name) _nameByAlpha3[a3] = c.name;
    if (a3 && code) _alpha2ByAlpha3[a3] = code;
  }
};

// Hydrate synchronously from cache so lookups work before the API responds.
try {
  if (typeof localStorage !== 'undefined') {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) _rebuild(JSON.parse(cached));
  }
} catch (e) {
  /* ignore malformed cache */
}

/**
 * Replace the registry (called by CountriesProvider after fetching from the DB).
 * @param {Array<{code:string, alpha3?:string, name:string}>} list
 */
export const setCountryRegistry = (list) => {
  _rebuild(list);
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(_list));
  } catch (e) {
    /* ignore quota errors */
  }
};

/** True once the registry has been populated (from cache or API). */
export const isCountryRegistryReady = () => _list.length > 0;

/**
 * Resolve any country code (alpha-2 or alpha-3) to its display name.
 * Falls back to the code itself when unknown or not yet loaded.
 */
export const getCountryName = (countryCode) => {
  if (!countryCode) return 'Unknown';
  const code = String(countryCode).toUpperCase();
  if (code === 'UNKNOWN' || code === 'N/A' || code === 'NA' || code === '-' || code === '') {
    return 'Unknown';
  }
  return _nameByCode[code] || (code.length === 3 ? _nameByAlpha3[code] : undefined) || code;
};

/** Resolve any code (alpha-2 or alpha-3) to alpha-2 (for flag rendering). */
const _toAlpha2 = (countryCode) => {
  const code = String(countryCode || '').toUpperCase();
  if (code.length === 2) return code;
  if (code.length === 3) return _alpha2ByAlpha3[code] || '';
  return '';
};

/** Public: resolve any code (alpha-2 or alpha-3) to lowercase alpha-2 for `fi fi-<code>` flag classes. Returns '' when unresolvable. */
export const getCountryAlpha2 = (countryCode) => _toAlpha2(countryCode).toLowerCase();

/** Formatted display e.g. 'US - United States'. */
export const getCountryDisplay = (countryCode) => {
  if (!countryCode) return 'Unknown';
  const code = String(countryCode).toUpperCase();
  const name = getCountryName(code);
  return name && name !== code ? `${code} - ${name}` : code;
};

/** Whether a code (alpha-2 or alpha-3) exists in the registry. */
export const isValidCountryCode = (countryCode) => {
  if (!countryCode || typeof countryCode !== 'string') return false;
  const code = countryCode.toUpperCase();
  return !!_nameByCode[code] || !!_nameByAlpha3[code];
};

/** All countries as sorted array of { code, alpha3, name }. */
export const getAllCountries = () =>
  _list
    .map((c) => ({ code: c.code, alpha3: c.alpha3, name: c.name }))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

/**
 * Country flag emoji from an alpha-2 or alpha-3 code. Returns 🌍 when unknown.
 */
export const getCountryFlag = (countryCode) => {
  const code = _toAlpha2(countryCode);
  if (code.length !== 2) return '🌍';
  try {
    const codePoints = [...code].map((char) => {
      const charCode = char.charCodeAt(0);
      if (charCode < 65 || charCode > 90) return null;
      return 0x1f1e6 + (charCode - 65);
    });
    if (codePoints.includes(null)) return '🌍';
    return String.fromCodePoint(...codePoints);
  } catch (error) {
    return '🌍';
  }
};
